import { Col, Form, message, Row, Space, Typography } from "antd";
// others
import dayjs from "dayjs";
import React, { Fragment, memo, useEffect, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { Empty } from "../../../../../assets/svg";
import { Booking } from "../../../../../types/bookings";
import { formatString } from "../../../../../utils/formatString";
import { DateField, InputField, RangePickerField, ReactSelectField } from "../../../../atoms/FormElement";
import { InputNumberFieldV2 as InputNumberField } from "../../../../atoms/FormElement/InputNumberField";
import { Popup } from "../../../../atoms/Popup";
import { convertToDayjs, convertToTitleCase, DATE_FORMATS, DecimalUtils, formatCurrency } from "../../../../libs/utils";
import Button from "../../../../molecules/Button";
import { getConfigData } from "../../../BookingForm/ducks/actions";
import { getOrgByID } from "../../../Maintenance/Tickets/ducks/actions";
import { getBookingDetail, RefreshRenwalData, updateBooking } from "../../ducks/actions";
import { updatePdcService } from "../../ducks/services";
import { AddCustomPaymentPlan } from "../AddCustomPaymentPlan";
import {
  disableSingleField,
  filterDescriptionLabel,
  filterPaymentPayload,
  payment_modes,
  rangePickerInitialValue,
} from "../utils";
import { Loading } from "./index"; 

const { Text } = Typography;

interface IProps {
  renewal?: boolean;
  parentState: (obj: object) => void;
  contractNo?: any;
  isCustomePaymentPlan?: boolean;
  setPaymentScheduler?: (obj) => void;
  customePlanLoading?: boolean;
}

let initialState = {
  visible: false,
  paymentSchedule: [],
  totalSum: 0,
  initialTotalSum: 0,
  bankList: [],
  triggerIndex: null,
  showAutoFillPopup: false,
  numberOfInstallments: 0,
};

const EditPaymentSchedule = (props: IProps) => {
  // hooks initialization
  // redux states
  const { bookingDetails } = useSelector((state: any) => state.newBooking);
  const configData = useSelector((state: any) => state.dashboard.configData);

  const { bookingTerms } = (bookingDetails || {}) as Booking;
  let isCustomePaymentSchedule = bookingDetails?.customPaymentSchedule?.length > 0 && props?.isCustomePaymentPlan;

  const docNoDebounceTimeout = useRef<NodeJS.Timeout | null>(null);

  const typeOfRenewal = useParams();
  const dispatch: any = useDispatch();

  let newRenewalType = typeOfRenewal["*"];

  const {
    watch,
    control,
    register,
    setValue,
    getValues,
    resetField,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();

  // states
  let [
    {
      visible,
      paymentSchedule,
      totalSum,
      initialTotalSum,
      bankList,
      triggerIndex,
      showAutoFillPopup,
      numberOfInstallments,
    },
    setState,
  ] = useState(initialState);

  const updateState = (state: {}) => setState((prevState: any) => ({ ...prevState, ...state }));

  useEffect(() => {
    if (!configData?.bankList) {
      dispatch(getConfigData(["bankList", "requiredBookingDocumentsList"]));
    }
    if (triggerIndex === null) return;

    const item = paymentSchedule[triggerIndex];
    const docNo = getValues(`documentNo-${triggerIndex}`);
    const bank = getValues(`bankName-${triggerIndex}`);

    if (item.description === "rent_installment" && docNo && bank?.value) {
      updateState({
        showAutoFillPopup: true,
      });
    } else {
      updateState({
        showAutoFillPopup: false,
      });
    }
  }, [triggerIndex]);

  const autoFillRemaining = (startIndex: number, startDocNo: string, bank: { label: string; value: string }) => {
    let num = parseInt(startDocNo, 10);
    if (isNaN(num)) return;

    for (let i = startIndex + 1; i < paymentSchedule.length; i++) {
      const item = paymentSchedule[i];
      if (item.description === "rent_installment") {
        num += 1;
        setValue(`documentNo-${i}`, String(num));
        setValue(`bankName-${i}`, bank);
      }
    }
  };

  let bookingPaymentSchedule = isCustomePaymentSchedule
    ? bookingDetails?.customPaymentSchedule
    : bookingDetails?.paymentSchedule;
  useEffect(() => {
    if (bookingPaymentSchedule) {
      isCustomePaymentSchedule && reset();
      updateState({
        paymentSchedule: filterDescriptionLabel(
          [...bookingPaymentSchedule],
          false,
          bookingDetails,
          props.renewal,
          newRenewalType,
        ),
        numberOfInstallments: bookingDetails?.leaseTerms?.noOfCheques,
      });
    }
  }, [isCustomePaymentSchedule, props?.customePlanLoading, bookingDetails?.customPaymentSchedule]);

  useEffect(() => {
    if (bookingDetails && bookingDetails?.orgId) {
      dispatch(getOrgByID(bookingDetails?.orgId));
    }
  }, []);

  useEffect(() => {
    if (configData && configData?.bankList && Object.keys(configData?.bankList)?.length > 0) {
      const bankList = configData?.bankList || {};
      const formattedBankList = Object?.entries(bankList as Record<string, string>).map(([_key, label]) => ({
        label,
        value: label.toLowerCase().replace(/\s+/g, "_"),
      }));
      updateState({
        bankList: formattedBankList,
      });
    }
  }, [configData?.bankList]);

  // Watch for all form fields changes
  const allValues = useWatch({
    control,
  });

  const sumRentInstallmentAmounts = (amounts: Array<string | number>): number => {
    if (!amounts.length) return 0;
    return DecimalUtils.toNumber(DecimalUtils.add(amounts));
  };

  const getSumTotal = (): number => {
    const amounts: string[] = [];
    if (!paymentSchedule?.length) return 0;

    paymentSchedule.forEach((item, index) => {
        if (
          item?.description === "rent_installment" &&
          getValues(`actualAmount-${index}`) &&
          (item?.chequeStatus === "pending" || item?.chequeStatus === "paid")
        ) {
          let valString = getValues(`actualAmount-${index}`)?.toString();
          let val = valString?.replace(/,/g, "");
          amounts.push(val);
        }
      });

    return sumRentInstallmentAmounts(amounts);
  };

  const calculateVatAmount = (amount: number | string, vatRate: string | number): number => {
    const rate = DecimalUtils.toNumber(vatRate);
    if (!rate) return 0;
    return DecimalUtils.toNumber(DecimalUtils.multiply(amount, DecimalUtils.multiply(rate, 0.01)));
  };

  const calculateTotalWithVat = (amount: number | string, vatRate: string | number): number => {
    return DecimalUtils.toNumber(DecimalUtils.add([amount, calculateVatAmount(amount, vatRate)]));
  };

  const handleFields = (name: string, value: any) => {
    const splitName = name && name?.split("-");
    const index = splitName && parseFloat(splitName[1]);
    const copy: Array<any> = [...paymentSchedule];

    switch (splitName && splitName[0]) {
      case "vatInPercentage":
        const actualAmount = DecimalUtils.toNumber(getValues(`actualAmount-${index}`)?.toString()?.replace(/,/g, ""));

        if (actualAmount) {
          setValue(`totalAmount-${index}`, calculateTotalWithVat(actualAmount, value));
        }

        break;
      case "actualAmount":
        const vat = DecimalUtils.toNumber(getValues(`vatInPercentage-${index || 0}`));

        if (vat) {
          setValue(`totalAmount-${index}`, calculateTotalWithVat(value, vat));
        } else setValue(`totalAmount-${index}`, DecimalUtils.toNumber(value));
        break;
      case "modeOfPayment":
        if (value?.value === "cash") {
          register(`documentNo-${splitName[1]}`, {
            value: "",
            disabled: true,
          });
          register(`bankName-${splitName[1]}`, {
            value: "",
            disabled: true,
          });
          register(`chequeDate-${splitName[1]}`, {
            value: "",
            disabled: true,
          });
          resetField(`chequeDate-${splitName[1]}`);
          resetField(`bankName-${splitName[1]}`, { defaultValue: "" });
          resetField(`documentNo-${splitName[1]}`, { defaultValue: "" });
        } else {
          setValue(`chequeDate-${splitName[1]}`, dayjs(copy[splitName[1]]?.chequeDate || copy[splitName[1]]?.dueDate));
        }

        copy[splitName[1]].modeOfPayment = value.value;
        updateState({ paymentSchedule: copy });
        break;
    }
  };

  useEffect(() => {
    const subscription = watch((value, { name }) => paymentSchedule?.length > 0 && handleFields(name, value[name]));
    return () => subscription.unsubscribe();
  }, [watch, paymentSchedule]);

  useEffect(() => {
    if (!paymentSchedule?.length) return;

    paymentSchedule.forEach((item, index) => {
      const actualAmount = DecimalUtils.toNumber(item?.actualAmount);
      const vatPct = DecimalUtils.toNumber(item?.vatInPercentage);

      if (actualAmount && vatPct) {
        setValue(`totalAmount-${index}`, calculateTotalWithVat(actualAmount, vatPct));
      } else if (actualAmount) {
        setValue(`totalAmount-${index}`, calCulateTotalAmount(item));
      }
    });
  }, [paymentSchedule]);

  useEffect(() => {
    const initialTotal = getSumTotal();
    updateState({ totalSum: initialTotal, initialTotalSum: initialTotal });
  }, [paymentSchedule]);

  useEffect(() => {
    const newTotalSum = getSumTotal();
    if (newTotalSum !== totalSum) {
      updateState({ totalSum: newTotalSum });
    }
  }, [allValues, totalSum, paymentSchedule]);

  const checkRentInstallmentTotalAmount = (items) => {
    const rentInstallments = items.filter((transaction) => transaction.description === "rent_installment");
    const amounts = rentInstallments.map((transaction) => transaction.actualAmount);
    return sumRentInstallmentAmounts(amounts);
  };

  const showError = () => {
    message.error("Sum of rent installments must be equal to contract rent. Please correct and try again");
    updateState({ visible: false });
    return;
  };

  const onSubmit = async (data: any) => {
    let leaseId = bookingDetails?.leaseId;
    let dataCopy = {
      updateDataOnly: true,
      leaseId: leaseId,
      paymentSchedule: filterPaymentPayload(data, paymentSchedule),
    };

    if (dataCopy?.paymentSchedule?.length > 0) {
      const totalAmount =
        bookingDetails?.leaseType === "extension"
          ? bookingDetails?.bookingTerms?.contractRent
          : bookingDetails?.bookingTerms?.renewalAmount || bookingDetails?.bookingTerms?.annualBaseRent;
      let calcSum = checkRentInstallmentTotalAmount(
        isCustomePaymentSchedule ? paymentSchedule : dataCopy?.paymentSchedule,
      );

      const start = dayjs(bookingDetails?.leaseTerms?.leaseStartDate);
      const end = dayjs(bookingDetails?.leaseTerms?.leaseEndDate);
      const isMultiYear = end.diff(start, "year", true) >= 1;

      if (!isCustomePaymentSchedule) {
        if (isMultiYear) {
          if (!DecimalUtils.equals(calcSum, initialTotalSum)) {
            showError();
            return;
          }
        } else {
          if (!DecimalUtils.equals(calcSum, totalAmount)) {
            showError();
            return;
          }
        }
      } else if (isCustomePaymentSchedule) {
        const total = getSumTotal();
        if (isMultiYear) {
          if (!DecimalUtils.equals(total, initialTotalSum)) {
            showError();
            return;
          }
        } else {
          if (!DecimalUtils.equals(total, calcSum)) {
            showError();
            return;
          }
        }
      }
    }

    try {
      let response = null;
      if (!props?.isCustomePaymentPlan) {
        response = await updatePdcService(dataCopy);
      } else {
        let customPaymentSchedule = dataCopy?.paymentSchedule;

        response = await dispatch(
          updateBooking({
            leaseId,
            body: { customPaymentSchedule: customPaymentSchedule },
          }),
        );

        isCustomePaymentSchedule && dispatch(getBookingDetail(leaseId));
      }

      updateState({ visible: false });
      message.success(response.data.message);
      props.parentState({ modalPayload: { open: false } });
      dispatch(RefreshRenwalData());
    } catch (error) {
      updateState({ visible: false });
      message.error(error.response?.data?.message);
    }
  };

  const textStyle = { fontSize: "12px" };
  const isPayment = paymentSchedule.length > 0;

  // using vat in percentage for edit payment schedule as per discussion with team, so keeping the function for future use if needed
  // const vatInPercentage = (data) => {
  //   // const totalAmount = Number(data?.totalAmount) || 0;
  //   // const actualAmount = Number(data?.actualAmount) || 0;
  //   // const vat = Number(data?.vat) || 0;
  //   const totalAmount = data?.totalAmount || 0;
  //   const actualAmount = data?.actualAmount || 0;
  //   const vat = data?.vat || 0;
  //   let totalVat = 0;

  //   if (actualAmount > 0) {
  //     if (totalAmount === actualAmount) {
  //       totalVat = 100 * ((totalAmount + vat) / actualAmount - 1);
  //     } else {
  //       totalVat = 100 * (totalAmount / actualAmount - 1);
  //     }
  //   }

  //   console.log("vatInPercentage", { totalAmount, actualAmount, vat, totalVat });
  //   return totalVat || 0;
  // };

  const calCulateTotalAmount = (data) => {
    if (data?.actualAmount === data?.totalAmount) {
      return DecimalUtils.toNumber(DecimalUtils.add([data?.totalAmount || 0, data?.vat || 0]));
    }
    return DecimalUtils.toNumber(data?.totalAmount || 0);
  };

  return (
    <div>
      {props?.isCustomePaymentPlan && <AddCustomPaymentPlan setPaymentScheduler={props?.setPaymentScheduler} />}
      {
        <Form
          layout="vertical"
          name="bookingform"
          scrollToFirstError
          onFinish={handleSubmit(onSubmit)}
          className="bf-main view-booking edit-payment-schedule"
        >
          <div style={{ width: "100%", overflow: "auto" }}>
            <Loading visible={visible || props?.customePlanLoading} />

            {isPayment && paymentSchedule?.length > 0 && (
              <Row
                gutter={[2, 2]}
                style={{
                  display: "flex",
                  flexWrap: "nowrap",
                  letterSpacing: "1.2px",
                  marginBottom: "8px",
                }}
              >
                <Col span={isCustomePaymentSchedule ? 5 : 3}>
                  <Text style={textStyle} className="fontWeight500 ag-default mb-1">
                    DESCRIPTION
                  </Text>
                </Col>
                <Col span={isCustomePaymentSchedule ? 5 : 2}>
                  <Text style={textStyle} className="fontWeight500 ag-default mb-2">
                    AMOUNT
                  </Text>
                </Col>
                <Col span={isCustomePaymentSchedule ? 4 : 2}>
                  <Text style={textStyle} className="fontWeight500 ag-default mb-2">
                    VAT %
                  </Text>
                </Col>
                <Col span={isCustomePaymentSchedule ? 5 : 2}>
                  <Text style={textStyle} className="fontWeight500 ag-default mb-2">
                    TOTAL AMOUNT
                  </Text>
                </Col>
                {!isCustomePaymentSchedule && (
                  <>
                    <Col span={3}>
                      <Text style={textStyle} className="fontWeight500 ag-default mb-2">
                        PAYMENT MODE
                      </Text>
                    </Col>
                    <Col span={2}>
                      <Text style={textStyle} className="fontWeight500 ag-default mb-2">
                        DOCUMENT NO
                      </Text>
                    </Col>
                    <Col span={2}>
                      <Text style={textStyle} className="fontWeight500 ag-default mb-2">
                        BANK NAME
                      </Text>
                    </Col>
                  </>
                )}
                <Col span={isCustomePaymentSchedule ? 5 : 3}>
                  <Text style={textStyle} className="fontWeight500 ag-default mb-2">
                    DUE DATE
                  </Text>
                </Col>
                <Col span={isCustomePaymentSchedule ? 5 : 3}>
                  <Text style={textStyle} className="fontWeight500 ag-default mb-2">
                    INVOICE DATE
                  </Text>
                </Col>

                {!isCustomePaymentSchedule && (
                  <>
                    <Col span={3}>
                      <Text style={textStyle} className="fontWeight500 ag-default mb-2">
                        CHEQUE DATE
                      </Text>
                    </Col>
                    <Col span={4}>
                      <Text style={textStyle} className="fontWeight500 ag-default mb-2">
                        INVOICE PERIOD (FROM/TO)
                      </Text>
                    </Col>
                    <Col span={2}>
                      <Text style={textStyle} className="fontWeight500 ag-default mb-2">
                        STATUS
                      </Text>
                    </Col>
                  </>
                )}
              </Row>
            )}

            {paymentSchedule.map((item: any, index: number) => {
              let filterInstallments = paymentSchedule?.filter((item) => item.description === "rent_installment");
              const modeOfPayment = item?.modeOfPayment
                ? payment_modes.filter((ele: any) => ele.value.toLowerCase() === item?.modeOfPayment.toLowerCase())
                : payment_modes[1];
              return (
                <Fragment key={item?.pdcId + item?._id}>
                  <Row gutter={2} style={{ position: "relative", display: "flex", flexWrap: "nowrap" }}>
                    <Col span={isCustomePaymentSchedule ? 5 : 3}>
                      <InputField
                        disabled={true}
                        control={control}
                        isRequired={true}
                        rules={{ required: "required" }}
                        fieldname={`description-${index}`}
                        initValue={formatString(item?.descriptionLabel)}
                        validate={errors[`description-${index}`] && "error"}
                      />
                    </Col>

                    <Col span={isCustomePaymentSchedule ? 5 : 2}>
                      <InputNumberField
                        isRequired
                        fieldname={`actualAmount-${index}`}
                        control={control}
                        format="currency"
                        inputNumberProps={{ min: 1 }}
                        defaultValue={item?.actualAmount > 0 ? Number(item?.actualAmount) : 0}
                        disabled={disableSingleField(item?.chequeStatus, item?.description, props.renewal)}
                      />
                    </Col>

                    <Col span={isCustomePaymentSchedule ? 4 : 2}>
                      <InputNumberField
                        isRequired
                        fieldname={`vatInPercentage-${index}`}
                        control={control}
                        inputNumberProps={{ min: 0, max: 100, precision: 2 }}
                        rules={{
                          required: "required",
                          min: { value: 0, message: "VAT must be at least 0" },
                          max: { value: 100, message: "VAT cannot exceed 100" },
                        }}
                        defaultValue={item?.vatInPercentage || 0}
                        disabled={disableSingleField(item?.chequeStatus, item?.description, props.renewal)}
                      />
                    </Col>

                    <Col span={isCustomePaymentSchedule ? 5 : 2}>
                      <InputNumberField
                        isRequired
                        fieldname={`totalAmount-${index}`}
                        control={control}
                        format="currency"
                        disabled
                        defaultValue={calCulateTotalAmount(item)}
                      />
                    </Col>

                    {!isCustomePaymentSchedule && (
                      <>
                        <Col span={3}>
                          <ReactSelectField
                            control={control}
                            isRequired={true}
                            initValue={modeOfPayment}
                            selectOption={payment_modes}
                            rules={{ required: "required" }}
                            fieldname={`modeOfPayment-${index}`}
                            validate={errors[`modeOfPayment-${index}`] && "error"}
                            iProps={{
                              isDisabled:
                                disableSingleField(item?.chequeStatus, item?.description, props.renewal) ||
                                item?.chequeStatus?.toLowerCase() === "transfer_in_progress" ||
                                (bookingDetails?.directDebit === true && item?.description === "rent_installment"),
                            }}
                          />
                        </Col>

                        <Col span={2}>
                          <InputField
                            control={control}
                            initValue={formatString(item?.documentNo)}
                            fieldname={`documentNo-${index}`}
                            validate={errors[`documentNo-${index}`] && "error"}
                            disabled={
                              item?.modeOfPayment === "cash" ||
                              disableSingleField(item?.chequeStatus, item?.description, props.renewal)
                            }
                            onChange={(e) => {
                              const val = e.target.value;
                              setValue(`documentNo-${index}`, val);

                              if (docNoDebounceTimeout.current) {
                                clearTimeout(docNoDebounceTimeout.current);
                              }

                              docNoDebounceTimeout.current = setTimeout(() => {
                                const bank = getValues(`bankName-${index}`);
                                if (
                                  filterInstallments?.length > 1 &&
                                  bank?.value &&
                                  item.description === "rent_installment" &&
                                  numberOfInstallments !== item?.installmentNumber &&
                                  index !== paymentSchedule.length - 1 // if last row or record then not trigger
                                ) {
                                  updateState({ triggerIndex: index });
                                }
                              }, 1000);
                            }}
                          />
                        </Col>

                        <Col span={2}>
                          <ReactSelectField
                            control={control}
                            initValue={{
                              label: item?.bankName,
                              value: item?.bankName,
                            }}
                            selectOption={bankList || []}
                            fieldname={`bankName-${index}`}
                            validate={errors[`bankName-${index}`] && "error"}
                            placeholder={"Select Bank"}
                            iProps={{
                              isDisabled:
                                item?.modeOfPayment === "cash" ||
                                disableSingleField(item?.chequeStatus, item?.description, props.renewal),
                            }}
                            onChange={(e) => {
                              setValue(`bankName-${index}`, e);
                              const docNo = getValues(`documentNo-${index}`);
                              if (
                                filterInstallments?.length > 1 &&
                                docNo &&
                                item.description === "rent_installment" &&
                                numberOfInstallments !== index &&
                                index !== paymentSchedule.length - 1 // if last row or record then not trigger
                              ) {
                                updateState({
                                  triggerIndex: index,
                                });
                              }
                            }}
                          />
                        </Col>
                      </>
                    )}

                    <Col span={isCustomePaymentSchedule ? 5 : 3}>
                      <DateField
                        control={control}
                        isRequired={true}
                        rules={{ required: "required" }}
                        fieldname={`dueDate-${index}`}
                        validate={errors[`dueDate-${index}`] && "error"}
                        initValue={item?.dueDate ? dayjs(item?.dueDate, "YYYY-MM-DD") : dayjs()}
                        disabled={disableSingleField(item?.chequeStatus, item?.description, props.renewal)}
                      />
                    </Col>

                    <Col span={isCustomePaymentSchedule ? 5 : 3}>
                      <DateField
                        control={control}
                        isRequired={true}
                        rules={{ required: "required" }}
                        fieldname={`invoiceDate-${index}`}
                        validate={errors[`invoiceDate-${index}`] && "error"}
                        initValue={convertToDayjs(item?.invoiceDate)}
                        disabled={disableSingleField(item?.chequeStatus, item?.description, props.renewal)}
                      />
                    </Col>

                    {!isCustomePaymentSchedule && (
                      <>
                        <Col span={3}>
                          <DateField
                            control={control}
                            fieldname={`chequeDate-${index}`}
                            validate={errors[`chequeDate-${index}`] && "error"}
                            initValue={
                              item?.chequeDate || bookingDetails?.leaseTerms?.dueDate || item?.dueDate
                                ? dayjs(
                                    item?.chequeDate || bookingDetails?.leaseTerms?.dueDate || item?.dueDate, //added fixes 8266
                                    "YYYY-MM-DD",
                                  )
                                : dayjs()
                            }
                            disabled={
                              item?.modeOfPayment === "cash" ||
                              disableSingleField(item?.chequeStatus, item?.description, props.renewal)
                            }
                          />
                        </Col>
                        <Col span={4}>
                          <RangePickerField
                            // isRequired={true}
                            control={control}
                            // disabled={!isEditAction && !isReceivePaymentAction}

                            setValue={setValue}
                            fieldname={`invoicePeriod-${index}`}
                            validate={errors.invoicePeriod && "error"}
                            initValue={rangePickerInitialValue(item, "payment_schedule_invice_period")}
                            rules={{ required: "Invoice Period Required" }}
                            iProps={{
                              format: DATE_FORMATS.display,
                              placeholder: ["Start Date", "End Date"],
                            }}
                            validMessage={errors.invoicePeriod && errors.invoicePeriod.message}
                          />
                        </Col>
                        <Col span={2}>
                          <InputField
                            control={control}
                            initValue={convertToTitleCase(item?.chequeStatus)}
                            fieldname={`paymentStatus-${index}`}
                            disabled={true}
                          />
                        </Col>
                      </>
                    )}
                  </Row>
                </Fragment>
              );
            })}

            <Row style={{ marginBottom: "20px" }}>
              <Col span={isCustomePaymentSchedule ? 5 : 3}>
                {totalSum > 0 && <p className="totalsum-text">Total Rent </p>}
              </Col>
              <Col span={isCustomePaymentSchedule ? 5 : 3}>
                {totalSum > 0 && <p className="totalsum-amount">{formatCurrency(totalSum)}</p>}
              </Col>
            </Row>
          </div>

          {!isPayment && (
            <Row style={{ justifyContent: "center" }}>
              {!props?.isCustomePaymentPlan && (
                <Empty width="200px" height="200px" style={{ alignContent: "center" }} />
              )}
            </Row>
          )}

          {isPayment && (
            <Row gutter={24} className="footer">
              {bookingDetails?.bookingTerms?.annualBaseRent > 0 && !isCustomePaymentSchedule && (
                <>
                  <Col span={12} className="display-amount">
                    {bookingDetails?.leaseType === "extension" ? (
                      <>
                        <p className="amount">{`${formatCurrency(
                          bookingDetails?.bookingTerms?.contractRent,
                        )} / `}</p>
                        <p className="year">
                          for {bookingDetails?.bookingTerms?.numberOfRenewalTerms} {bookingTerms?.durationType}
                        </p>
                      </>
                    ) : (
                      <>
                        <p className="amount">{`${formatCurrency(
                          bookingDetails?.bookingTerms?.renewalAmount || bookingDetails?.bookingTerms?.annualBaseRent,
                        )} / `}</p>
                        <p className="year">Year</p>
                      </>
                    )}
                  </Col>
                </>
              )}

              {isCustomePaymentSchedule && (
                <Col span={12} className="display-amount">
                  <>
                    <p className="amount">{`${formatCurrency(
                      checkRentInstallmentTotalAmount(bookingDetails?.customPaymentSchedule),
                    )} / `}</p>
                    <p className="year">Year</p>
                  </>
                </Col>
              )}

              <Space size="middle">
                <Button
                  label="Cancel"
                  className="bf-discard-button"
                  onClick={() => props.parentState({ modalPayload: { open: false } })}
                />

                <Button
                  disabled={props?.customePlanLoading}
                  type="submit"
                  label="Update"
                  className="cc-fotter_next-button"
                />
              </Space>
            </Row>
          )}

          <Popup
            visibility={showAutoFillPopup}
            titleColor={{ color: "#FFFFFF", fontWeight: 600 }}
            onCancel={() => {
              updateState({
                showAutoFillPopup: false,
                triggerIndex: null,
              });
            }}
            title="Auto-populate remaining document numbers?"
            content={
              <Space align="center" style={{ width: "100%", justifyContent: "center" }} size="large">
                <Button
                  label="NO"
                  className="bf-discard-button"
                  onClick={() => {
                    updateState({
                      showAutoFillPopup: false,
                      triggerIndex: null,
                    });
                  }}
                />
                <Button
                  label="YES"
                  className="cc-fotter_next-button"
                  onClick={() => {
                    const docNo = getValues(`documentNo-${triggerIndex}`);
                    const bank = getValues(`bankName-${triggerIndex}`);
                    autoFillRemaining(triggerIndex, docNo, bank);
                    updateState({
                      showAutoFillPopup: false,
                      triggerIndex: null,
                    });
                  }}
                />
              </Space>
            }
          />
        </Form>
      }
    </div>
  );
};

export default memo(EditPaymentSchedule);
